import { ColumnDef, createColumnHelper, Row } from '@tanstack/react-table';
import { useModelService, Measure } from 'services/model/model.service';
import { IntlShape, useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';
import { buildDrillDownTableCoreColumns } from 'pages/trading/outlier/DrilldownCoreColumns';
import { DrillDownTableDateGmvValues } from 'pages/trading/outlier/DrillDownTableDateRangeRow';
import { MessageSentiment } from 'pages/trading/model';
import { ShareBar } from 'pages/insights-pro/common/reports/ShareBar';
import { TRADING_ROOT } from 'pages/trading/Trading';
import { DataTableContextProvider } from 'components/table/context/DataTableContextProvider';
import { DataTable } from 'components/table/DataTable';
import { useCoreDrilldownExportConfigOptions } from 'pages/trading/outlier/DrilldownExportUtil';

export interface MarketplacesRow extends DrillDownTableDateGmvValues {
  chnMarketplace: string;
}

const columnHelper = createColumnHelper<MarketplacesRow>();
const buildColumns = (headers: string[], intl: IntlShape, measure: Measure) => [
  columnHelper.accessor('chnMarketplace', {
    id: 'chnMarketplace',
    header: () => headers[0],
    cell: info => <div className="flex gap-2 items-center">{info.getValue()}</div>,
  }),
  columnHelper.accessor(row => row.share, {
    id: 'share',
    header: () => intl.formatMessage({ id: 'trading.drilldown.column.share' }),
    cell: info => <ShareBar width={info.getValue() * 100} color="#314968" />,
  }),
  ...buildDrillDownTableCoreColumns(intl, measure),
];

export interface MarketplacesTableProps {
  rows: MarketplacesRow[];
  measure: Measure;
  sentiment?: MessageSentiment;
  detectionDate?: string;
}

export function MarketplacesTable({
  rows,
  measure,
  sentiment = 'NEUTRAL',
  detectionDate = undefined,
}: MarketplacesTableProps) {
  const { getAttributes } = useModelService();
  const [prdCategory] = getAttributes(['chnMarketplace']);
  const navigate = useNavigate();
  const location = useLocation();
  const intl = useIntl();

  const onRowClick = (row: Row<unknown>) => {
    const castedRow = row as Row<MarketplacesRow>;
    navigate(`${TRADING_ROOT}/drilldown/chnMarketplace/${castedRow.original.chnMarketplace}${location.search}`);
  };

  const columns = buildColumns([prdCategory.name], intl, measure) as ColumnDef<unknown>[];

  const coreDrilldownExportConfigOptions = useCoreDrilldownExportConfigOptions('marketplaces');

  return (
    <DataTableContextProvider>
      <DataTable
        testId="trading-drilldown-marketplaces-table"
        titleId="insights.marketplaces"
        subTitleDecorator={header =>
          `${header}${detectionDate ? ` ${intl.formatMessage({ id: 'trading.drilldown.detection.date' })} ${detectionDate}` : ''}`
        }
        rows={rows}
        columns={columns}
        onRowClick={onRowClick}
        exportConfigOptions={coreDrilldownExportConfigOptions}
        defaultSortingState={[{ id: 'onDayComparisonGmv', desc: sentiment !== 'NEGATIVE' }]}
      />
    </DataTableContextProvider>
  );
}
